import {
  askConfirm,
  type DialogNotice,
  type DialogTable,
  showAlert,
  showError,
} from "@hereby/components";
import type { HerebyClient } from "@hereby/sdk";
import Alpine from "alpinejs";

/**
 * 「예약된 변경」 관리 페이지 (MED-827, 설계 §4-6).
 *
 * 예약 액션(계약 시행일 변경 · 시트 칸 예약)을 종류·상태·시행월·소유자·직원으로 걸러 보고,
 * 취소 · 지금 실행 · 검사(dry-run) · 이관을 한다. 아래 읽기 전용 절은 예약 액션을 거치지 않는
 * 원천 시행일(조직 상수 · 상시 시트 새 버전 · 고정 수당 시작/종료)을 같은 달 기준으로 보여 준다.
 * FAILED 는 상단 배지 + 목록 첫 정렬. 실행 권한은 언제나 예약 소유자 기준(서버가 판정).
 */

export interface ScheduledActionRow {
  id: string;
  kind: string;
  status: string;
  origin: string;
  effectiveDate: string;
  note: string | null;
  ownerId: string;
  ownerName: string | null;
  /** 소유자가 퇴사·비활성 — 시행일에 실패하니 이관해야 한다 */
  ownerUnavailable?: boolean;
  employeeId: string | null;
  employeeName: string | null;
  employeeNumber: string | null;
  target: Record<string, unknown>;
  resultRef: Record<string, unknown> | null;
  lastError: {
    code: string;
    message: string;
    at?: string;
    acknowledgedAt?: string;
  } | null;
  lastDryRunAt: string | Date | null;
  lastDryRunResult: {
    ok: boolean;
    affected: number;
    warnings: string[];
    error?: string;
  } | null;
  createdAt: string | Date;
  /** 변경 전 값 — 계약 변경은 서버가 지금 계약(또는 적용 때 닫은 옛 계약)에서 읽어 준다 */
  beforeValues?: Record<string, unknown> | null;
  appliedAt?: string | Date | null;
  cancelledAt?: string | Date | null;
}

/** 상세 — 목록 한 줄 + 만든/적용한/취소한 사람 + 이관 이력 */
export interface ScheduledActionDetail extends ScheduledActionRow {
  createdByName: string | null;
  appliedByName: string | null;
  cancelledByName: string | null;
  transfers: Array<{
    at: string | Date;
    fromName: string | null;
    toName: string | null;
    byName: string | null;
  }>;
}

export const KIND_LABELS: Record<string, string> = {
  contract_change: "계약 시행일 변경",
  sheet_cell: "시트 칸 예약",
  hr_action: "인사처리 시행",
  leave_basis_transition: "연차 기준 전환",
  worktime_auto_close: "근로시간 자동 마감",
  leave_promotion: "연차 사용촉진",
  new_employee_row: "신규 입사자 행 추가",
};

/** 기록용 kind(T10) — 잡이 남긴 흔적. 취소·실행·이관·검사 버튼을 두지 않는다. */
export const RECORD_KINDS = new Set([
  "hr_action",
  "leave_basis_transition",
  "worktime_auto_close",
  "leave_promotion",
  "new_employee_row",
]);
export const isRecordKind = (kind: string): boolean => RECORD_KINDS.has(kind);

export const STATUS_LABELS: Record<string, string> = {
  scheduled: "예약됨",
  proposed: "확인 대기",
  applied: "적용됨",
  failed: "실패",
  skipped: "건너뜀",
  cancelled: "취소됨",
};

export const STATUS_TONES: Record<string, string> = {
  scheduled: "ds-label--blue",
  proposed: "ds-label--yellow",
  applied: "ds-label--green",
  failed: "ds-label--red",
  skipped: "ds-label--gray",
  cancelled: "ds-label--gray",
};

export const ORIGIN_LABELS: Record<string, string> = {
  user: "직접",
  ai: "AI 제안",
  system: "승급 승인 등",
};

/** 액션 한 줄의 「무엇이 바뀌나」 — 종류별로. */
export function describeAction(row: Pick<ScheduledActionRow, "kind" | "target">): string {
  const t = row.target ?? {};
  if (isRecordKind(row.kind)) {
    return String(t.summary ?? "");
  }
  if (row.kind === "sheet_cell") {
    const v = t.value;
    const shown =
      v === null || v === undefined || v === ""
        ? "(비움)"
        : typeof v === "number"
          ? v.toLocaleString("ko-KR")
          : String(v);
    return `「${(t.sheetName as string) ?? (t.sheetSlug as string) ?? ""}」 ${(t.columnLabel as string) ?? (t.columnKey as string) ?? ""} → ${shown}`;
  }
  const patch = (t.patch as Record<string, unknown> | undefined) ?? {};
  const labels: Record<string, string> = {
    baseSalary: "기본급",
    hobongAmount: "호봉인상분",
    longevityAmount: "장기근속 단가",
    hourlyRate: "통상시급",
    contractType: "계약유형",
    wageType: "임금유형",
    salaryGradeId: "호봉",
  };
  const parts = Object.entries(patch)
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => {
      if (k === "salaryGradeId") {
        return v === null ? "호봉 해제" : "호봉 배정";
      }
      if (k === "allowances" && v && typeof v === "object") {
        return `수당 ${Object.keys(v as object).join(", ")}`;
      }
      return `${labels[k] ?? k} ${typeof v === "number" ? v.toLocaleString("ko-KR") : String(v)}`;
    });
  return parts.join(" · ") || "계약 변경";
}

export type StatusFilter = "open" | "all" | "failed" | "proposed" | "applied";

/** 상단 칩 — 필터와 무관한 건수를 누르면 그 묶음만 본다 */
export type SummaryChip = "" | "failed" | "proposed" | "dueSoon" | "ownerUnavailable";

/** 위쪽 탭 — 칩 묶음 + 진행 중(빈 칩) + 끝난 것(상태 applied·skipped·cancelled) */
export type StatusTab = Exclude<SummaryChip, ""> | "open" | "done" | "month";

export interface PageFilter {
  chip?: SummaryChip;
  kind: string;
  status: StatusFilter;
  month: string;
  ownerId: string;
  search: string;
}

/** 알림·시트 화면에서 넘어온 좁히기 — 필터 줄과 따로 보여 주고 「해제」로 푼다. */
export interface PageScope {
  id?: string;
  employeeId?: string;
  sheetSlug?: string;
  label?: string;
}

export function statusesFor(status: StatusFilter): string[] | undefined {
  switch (status) {
    case "open":
      return ["scheduled", "proposed", "failed"];
    case "failed":
      return ["failed"];
    case "proposed":
      return ["proposed"];
    case "applied":
      return ["applied", "skipped", "cancelled"];
    default:
      return undefined;
  }
}

/** 서버 page 입력 — 빈 값은 보내지 않는다. 진행 중 보기에서는 「확인함」 처리한 기록 실패를 뺀다. */
export function buildPageInput(
  filter: PageFilter,
  scope: PageScope | null,
  page: number,
  pageSize: number,
) {
  if (scope?.id) {
    return { id: scope.id, page: 1, pageSize };
  }
  const search = filter.search.trim();
  const chip = filter.chip ?? "";
  return {
    kind: filter.kind || undefined,
    status:
      chip === "failed"
        ? ["failed"]
        : chip === "proposed"
          ? ["proposed"]
          : chip === "dueSoon" || chip === "ownerUnavailable"
            ? statusesFor("open")
            : statusesFor(filter.status),
    dueWithinDays: chip === "dueSoon" ? 7 : undefined,
    ownerUnavailable: chip === "ownerUnavailable" ? true : undefined,
    month: /^\d{4}-\d{2}$/.test(filter.month) ? filter.month : undefined,
    ownerId: filter.ownerId || undefined,
    search: search || undefined,
    employeeId: scope?.employeeId,
    sheetSlug: scope?.sheetSlug,
    excludeAcknowledged:
      chip || filter.status === "open" || filter.status === "failed" ? true : undefined,
    page,
    pageSize,
  };
}

/**
 * 시행일이 아직 안 온 계약 변경인가 — 서버가 미리 실행을 거절하므로(미래 계약을 미리 확정하지 않는다) 「지금 실행」을
 * 보이지 않고, 실패 건은 「다시 예약」(시행일에 자동 적용)으로 바꿔 부른다.
 */
export function isFutureContract(
  row: Pick<ScheduledActionRow, "kind" | "effectiveDate">,
  todayIso: string,
): boolean {
  return row.kind === "contract_change" && String(row.effectiveDate).slice(0, 10) > todayIso;
}

const CONTRACT_FIELD_LABELS: Record<string, string> = {
  baseSalary: "기본급",
  hobongAmount: "호봉인상분",
  longevityAmount: "장기근속 단가",
  hourlyRate: "통상시급",
  contractType: "계약유형",
  wageType: "임금유형",
  salaryGradeId: "호봉",
  standardWeeklyHours: "주 소정근로시간",
  maxWeeklyHours: "주 최대근로시간",
};
const ALLOWANCE_LABELS: Record<string, string> = {
  meal: "식대",
  transportation: "교통비",
  position: "직책수당",
};

/** 빈 값 표기 — 표에서 이 칸은 취소선·굵게를 걸지 않는다(없던 값이 바뀌는 값처럼 읽히지 않게) */
export const EMPTY_CHANGE_VALUE = "(비움)";

/** 값 한 칸 — 빈 값은 (비움), 숫자는 천 단위 쉼표 */
export function formatChangeValue(v: unknown): string {
  if (v === null || v === undefined || v === "") {
    return EMPTY_CHANGE_VALUE;
  }
  return typeof v === "number" ? v.toLocaleString("ko-KR") : String(v);
}

export interface ChangePart {
  label: string;
  /** 변경 전 — 모르면 null(화면에 「-」) */
  before: string | null;
  after: string;
}

/**
 * 「바뀌는 것」을 항목·전·후로 — 표·카드·상세가 같은 함수를 쓴다. 칸 예약의 전 값은 예약 당시 값(expectedBefore),
 * 계약 변경의 전 값은 서버가 붙인 beforeValues. 자동 처리 기록은 요약 한 줄(전·후 없음).
 */
export function changeParts(
  row: Pick<ScheduledActionRow, "kind" | "target" | "beforeValues">,
): ChangePart[] {
  const t = row.target ?? {};
  if (isRecordKind(row.kind)) {
    return [{ label: String(t.summary ?? ""), before: null, after: "" }];
  }
  if (row.kind === "sheet_cell") {
    const sheet = (t.sheetName as string) ?? (t.sheetSlug as string) ?? "";
    const col = (t.columnLabel as string) ?? (t.columnKey as string) ?? "";
    return [
      {
        label: `「${sheet}」 ${col}`,
        before: t.expectedBefore === undefined ? null : formatChangeValue(t.expectedBefore),
        after: formatChangeValue(t.value),
      },
    ];
  }
  const patch = (t.patch as Record<string, unknown> | undefined) ?? {};
  const before = row.beforeValues ?? {};
  const parts: ChangePart[] = [];
  // 수당은 맨 뒤로 — 기본급·시급 같은 주 항목을 먼저 읽게
  const entries = Object.entries(patch).sort(
    ([a], [b]) => Number(a === "allowances") - Number(b === "allowances"),
  );
  for (const [k, v] of entries) {
    if (v === undefined) {
      continue;
    }
    if (k === "allowances" && v && typeof v === "object") {
      const prev = (before.allowances ?? {}) as Record<string, unknown>;
      for (const [ak, av] of Object.entries(v as Record<string, unknown>)) {
        parts.push({
          label: ALLOWANCE_LABELS[ak] ?? `수당 ${ak}`,
          before: ak in prev ? formatChangeValue(prev[ak]) : null,
          after: formatChangeValue(av),
        });
      }
      continue;
    }
    if (k === "salaryGradeId") {
      // 호봉은 id 라 값 대신 배정/해제로만 — 금액은 기본급·호봉인상분 줄이 보여 준다
      parts.push({ label: "호봉", before: null, after: v === null ? "해제" : "배정" });
      continue;
    }
    parts.push({
      label: CONTRACT_FIELD_LABELS[k] ?? k,
      before: k in before ? formatChangeValue(before[k]) : null,
      after: formatChangeValue(v),
    });
  }
  return parts.length ? parts : [{ label: "계약 변경", before: null, after: "" }];
}

/** 시행월 드롭다운 — 오늘 달 기준 앞뒤 12달(최근이 위) */
export function monthOptions(todayIso: string, span = 12): Array<{ value: string; label: string }> {
  const y = Number(todayIso.slice(0, 4));
  const m = Number(todayIso.slice(5, 7));
  const out: Array<{ value: string; label: string }> = [];
  for (let d = span; d >= -span; d--) {
    const total = y * 12 + (m - 1) + d;
    const yy = Math.floor(total / 12);
    const mm = (total % 12) + 1;
    out.push({ value: `${yy}-${String(mm).padStart(2, "0")}`, label: `${yy}년 ${mm}월` });
  }
  return out;
}

export type PrimaryAction = "acknowledge" | "run" | "approve" | "dryRun" | "detail";

/**
 * 줄마다 글자로 보이는 주 동작 하나 — 나머지는 「⋯」 메뉴. 지금 할 일이 상태마다 다르다:
 * 실패=다시 실행(미래 계약은 다시 예약) · 확인 대기=검토(승인) · 예약됨=검사 · 기록 실패=확인함 · 끝난 것=상세.
 */
export function primaryActionFor(
  row: Pick<ScheduledActionRow, "kind" | "status" | "lastError">,
): PrimaryAction | null {
  if (isRecordKind(row.kind)) {
    return row.status === "failed" && !row.lastError?.acknowledgedAt ? "acknowledge" : "detail";
  }
  switch (row.status) {
    case "failed":
      return "run";
    case "proposed":
      return "approve";
    case "scheduled":
      return "dryRun";
    default:
      return "detail";
  }
}

export type RowMenuKey =
  | "dryRun"
  | "run"
  | "approve"
  | "acknowledge"
  | "openSource"
  | "transfer"
  | "cancel";

export interface RowMenuItem {
  key: RowMenuKey;
  label: string;
  /** 더보기 열의 아이콘 버튼(레지스트리 이름) */
  icon: string;
  enabled: boolean;
  /** 못 고르는 이유 — 항목 아래 작은 글자로. 고를 수 있으면 null */
  reason: string | null;
  danger?: boolean;
}

const OPEN = ["scheduled", "proposed", "failed"];

/**
 * 더보기 열의 아이콘 버튼 — 동작 8개를 늘 같은 순서로 정하고, 지금 할 수 있는지(enabled)와 못 하는 이유(reason)를 함께 준다.
 * 화면은 할 수 있는 것만 꺼내 보이고(못 쓰는 버튼은 비노출), 이유는 테스트·상세 안내에 쓴다.
 */
export function rowMenuItems(
  row: Pick<
    ScheduledActionRow,
    "kind" | "status" | "effectiveDate" | "lastError" | "resultRef" | "target" | "employeeId"
  >,
  todayIso: string,
): RowMenuItem[] {
  const record = isRecordKind(row.kind);
  const open = OPEN.includes(row.status);
  const finished = "끝난 예약이에요.";
  const recordReason = "자동 처리 기록은 원래 화면에서 처리해요.";
  const future = isFutureContract(row, todayIso);
  const Icons: Record<RowMenuKey, string> = {
    dryRun: "ic-shield-check",
    run: "ic-redo",
    approve: "ic-check",
    acknowledge: "ic-check-circle",
    openSource: "ic-external-link",
    transfer: "ic-user-transfer",
    cancel: "ic-ban",
  };
  const item = (
    key: RowMenuKey,
    label: string,
    reason: string | null,
    danger = false,
  ): RowMenuItem => ({ key, label, icon: Icons[key], enabled: reason === null, reason, danger });

  const runLabel = row.status === "failed" ? (future ? "다시 예약" : "다시 실행") : "지금 실행";
  const runReason = record
    ? recordReason
    : open
      ? row.status === "proposed"
        ? "확인 대기는 먼저 검토(승인)해요."
        : row.status === "scheduled" && future
          ? "계약 변경은 시행일 전에 실행할 수 없어요."
          : null
      : finished;

  const ackReason = record
    ? row.status !== "failed"
      ? "실패한 기록이 아니에요."
      : row.lastError?.acknowledgedAt
        ? "이미 확인함으로 넘겼어요."
        : null
    : "자동 처리 기록의 실패에만 써요.";

  const sheetViewId = (row.resultRef?.sheetViewId ?? row.target?.sheetViewId) as string | undefined;
  const sourceLabel =
    row.kind === "sheet_cell"
      ? "시트에서 열기"
      : row.kind === "contract_change"
        ? "직원 정보 열기"
        : "원래 화면 열기";
  const sourceReason = record
    ? recordReason
    : row.kind !== "sheet_cell"
      ? "직원 정보는 대상 열의 이름을 눌러 열어요."
      : sheetViewId
        ? null
        : "시행월 시트가 아직 없어요.";

  const manageReason = record ? recordReason : open ? null : finished;

  // 상세는 행을 누르면 열리고, 직원 정보는 대상 열의 이름이 연다 — 둘 다 버튼으로 두지 않는다.
  // 검사(dry-run)는 상세 창의 「예약 취소」 옆에 둔다(작업 열의 「검사」 글자 버튼은 그대로)
  return [
    {
      ...item("run", runLabel, runReason),
      icon: runLabel === "다시 예약" ? "ic-calendar-clock" : "ic-redo",
    },
    item(
      "approve",
      "검토(승인)",
      record ? recordReason : row.status === "proposed" ? null : "확인 대기일 때만 승인해요.",
    ),
    item("acknowledge", "확인함", ackReason),
    item("openSource", sourceLabel, sourceReason),
    item("transfer", "소유자 이관", manageReason),
    item("cancel", "예약 취소", manageReason, true),
  ];
}

export interface DryRunResult {
  ok: boolean;
  affected: number;
  diff: Array<{ target: string; before: unknown; after: unknown }>;
  warnings: string[];
  error?: string;
}

/**
 * 적용 미리보기(dry-run) 한 줄 — 서버 diff 의 항목은 계약 열 이름(baseSalary…)이나 한글 설명이 섞여 오고, 금액은
 * numeric 열이라 "4970000.00" 같은 문자열로 온다. 화면 말로 바꾸고 숫자는 천 단위로.
 */
export function previewLine(d: { target: string; before: unknown; after: unknown }): string {
  const r = previewRow(d);
  return `${r.label}: ${r.before} → ${r.after}`;
}

/** 적용 미리보기 한 항목을 표 한 줄(항목 · 지금 · 적용 후)로 — 실행 확인 창의 표가 쓴다 */
export function previewRow(d: { target: string; before: unknown; after: unknown }): {
  label: string;
  before: string;
  after: string;
} {
  const label = CONTRACT_FIELD_LABELS[d.target] ?? ALLOWANCE_LABELS[d.target] ?? d.target;
  const val = (v: unknown) => {
    if (typeof v === "string" && /^-?\d+(\.\d+)?$/.test(v)) {
      return formatChangeValue(Number(v));
    }
    return formatChangeValue(v);
  };
  return { label, before: val(d.before), after: val(d.after) };
}

/**
 * 미리보기 결과를 공용 확인 창의 표 · 안내로 — 실행·승인 확인 창과 「미리보기」 창이 같이 쓴다.
 * `when` 은 표 제목의 시점: "now"(지금 적용 · 시트 승인) · "effective"(계약 승인 — 시행일에 적용되지만
 * 계산은 오늘 기준이라 그 사이 다른 변경이 끼면 달라질 수 있음을 제목에 밝힌다).
 */
export function previewDialogBody(
  r: DryRunResult | null,
  error: string | null,
  when: "now" | "effective" = "now",
): { table?: DialogTable; notice?: DialogNotice } {
  if (error !== null || !r) {
    return {
      notice: { tone: "danger", text: `미리보기를 하지 못했어요: ${error ?? "사유 없음"}` },
    };
  }
  if (!r.ok) {
    return {
      notice: { tone: "danger", text: `지금 적용하면 실패해요: ${r.error ?? "사유 없음"}` },
    };
  }
  const head =
    when === "effective"
      ? r.diff.length
        ? "시행일에 이렇게 바뀌어요(오늘 기준)"
        : "시행일에 바뀌는 값이 없어요(오늘 기준)"
      : r.diff.length
        ? "지금 적용하면 이렇게 바뀌어요"
        : "지금 적용해도 바뀌는 값이 없어요";
  const shown = r.diff.slice(0, 8).map(previewRow);
  return {
    // 참고 사항(시트가 아직 없음 · 잠긴 시트 등) — 통과여도 빠뜨리면 「바뀌는 값이 없어요」만 보고 오해한다
    notice: r.warnings.length ? { tone: "warning", text: r.warnings.join("\n") } : undefined,
    table: {
      heading: head,
      columns: ["항목", "지금", when === "effective" ? "시행 후" : "적용 후"],
      rows: shown.map((d) => [d.label, d.before, d.after]),
      columnStyles: [null, "before", "after"],
      emptyText: EMPTY_CHANGE_VALUE,
      more: r.diff.length > 8 ? `외 ${r.diff.length - 8}개 항목` : undefined,
    },
  };
}

/** 페이지 번호 창 — DS 페이지네이션 규칙대로 최대 10개, 양끝에서 클램프. */
export function pageWindow(page: number, totalPages: number, size = 10): number[] {
  if (totalPages <= 0) {
    return [];
  }
  // DS 공식(pagination.md): end = min(total, max(1, cur-4) + 9) · start = max(1, end - 9)
  const end = Math.min(totalPages, Math.max(1, page - 4) + size - 1);
  const start = Math.max(1, end - size + 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

/** CSV 한 칸 — 쉼표·따옴표·줄바꿈이 있으면 따옴표로 감싸고, 수식으로 읽히는 첫 글자(= + - @)는 막는다. */
function csvCell(v: unknown): string {
  let text = v === null || v === undefined ? "" : String(v);
  if (/^[=+\-@\t\r]/.test(text)) {
    text = `'${text}`;
  }
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** 엑셀이 한글을 깨뜨리지 않게 BOM 을 붙인 CSV. */
export function toCsv(rows: unknown[][]): string {
  return `\uFEFF${rows.map((r) => r.map(csvCell).join(",")).join("\r\n")}`;
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export interface ScheduledChangesSummary {
  failed: number;
  proposed: number;
  scheduled: number;
  dueSoon: number;
  ownerUnavailable: number;
}

/** DS 페이지네이션(pagination.md #page-size) — 30 / 50 / 100 / 전체(최대 1,000) 네 개 고정, 기본 30 */
export const PAGE_SIZE_ALL = 1000;
export const PAGE_SIZE_OPTIONS = [
  { value: 30, label: "30건씩" },
  { value: 50, label: "50건씩" },
  { value: 100, label: "100건씩" },
  { value: PAGE_SIZE_ALL, label: "전체" },
];
const PAGE_SIZE_KEY = "admin:pageSize:scheduled-changes";

function readPageSize(): number {
  try {
    const raw = Number(localStorage.getItem(PAGE_SIZE_KEY));
    return PAGE_SIZE_OPTIONS.some((o) => o.value === raw) ? raw : 30;
  } catch {
    return 30;
  }
}

/** 알림·시트 화면이 이 페이지를 특정 건으로 열 때 쏘는 창 이벤트(admin app.ts 가 쏜다). */
export const FOCUS_EVENT = "scheduled-changes:focus";
/** 시트 칸 예약의 「시트 열기」 — admin app.ts 가 시트 임베드를 그 시트로 연다. */
export const OPEN_SHEET_EVENT = "scheduled-changes:open-sheet";

let measureCtx: CanvasRenderingContext2D | null | undefined;
function measureCanvas(): CanvasRenderingContext2D | null {
  if (measureCtx === undefined) {
    measureCtx = document.createElement("canvas").getContext("2d");
  }
  return measureCtx;
}

export function scheduledChangesPage() {
  return {
    loadedOnce: false,
    loading: false,
    loadError: "" as string,
    rows: [] as ScheduledActionRow[],
    total: 0,
    page: 1,
    pageSize: readPageSize(),
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    owners: [] as Array<{ id: string; name: string }>,
    summary: null as ScheduledChangesSummary | null,
    filter: {
      chip: "",
      kind: "",
      status: "open",
      month: "",
      ownerId: "",
      search: "",
    } as PageFilter,
    scope: null as PageScope | null,
    busyId: "" as string,
    /** 「대상」 열 폭(px) — 지금 쪽의 가장 긴 이름·사번에 맞춘다(fitTargetCol). 재기 전 기본값 */
    targetColWidth: 96,
    exporting: false,
    overview: null as Record<string, unknown> | null,
    overviewLoading: false,
    overviewMonth: "" as string,
    transferTarget: null as ScheduledActionRow | null,
    transferSearch: "",
    transferCandidates: [] as Array<{ id: string; name: string; employeeNumber: string }>,
    transferBusy: false,
    /** 탭이 바꾸는 화면 — 예약 목록 / 이 달 시행 요약(원천 변경). 연차 탭의 서브탭처럼 화면을 통째로 바꾼다 */
    view: "list" as "list" | "month",
    /** 상세 창 — 목록 위에 일반 모달로 뜬다(목록·필터·스크롤은 그대로) */
    detailRow: null as ScheduledActionRow | null,
    detail: null as ScheduledActionDetail | null,
    detailLoading: false,
    searchTimer: 0 as ReturnType<typeof setTimeout> | 0,
    /** 마지막 요청 번호 — 늦게 도착한 옛 응답(탭 첫 로드 vs 알림 좁히기)이 새 결과를 덮지 않게 */
    requestSeq: 0,
    focusListener: null as ((e: Event) => void) | null,

    get sdk(): HerebyClient {
      return Alpine.store("hereby").sdk as HerebyClient;
    },
    /** 예약에는 급여 금액이 들어 있어 목록·요약도 급여 조회(조직 전체) 권한이 있어야 본다(서버와 같은 기준) */
    get canSeePage(): boolean {
      const store = Alpine.store("hereby") as { hasPermission(p: string): boolean };
      return store.hasPermission("payroll:read.all") || store.hasPermission("payroll:read");
    },
    get canSeeOverview(): boolean {
      return this.canSeePage;
    },
    get today(): string {
      return Alpine.store("hereby").todayLocal() as string;
    },
    isFutureContract(row: ScheduledActionRow): boolean {
      return isFutureContract(row, this.today);
    },
    /** 행의 실행 버튼 — 미래 계약의 예약 상태는 숨김, 미래 계약의 실패는 「다시 예약」 */
    runLabel(row: ScheduledActionRow): string {
      if (row.status !== "failed") {
        return "지금 실행";
      }
      return this.isFutureContract(row) ? "다시 예약" : "다시 실행";
    },
    canRunNow(row: ScheduledActionRow): boolean {
      return row.status === "failed" || (row.status === "scheduled" && !this.isFutureContract(row));
    },
    get totalPages(): number {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
    get pageRange(): number[] {
      return pageWindow(this.page, this.totalPages);
    },
    get failedCount(): number {
      return this.summary?.failed ?? 0;
    },
    get proposedCount(): number {
      return this.summary?.proposed ?? 0;
    },
    /** 필터를 하나라도 걸었나 — 빈 결과 문구를 「없음」과 「조건에 맞는 것 없음」으로 가른다 */
    get hasFilter(): boolean {
      const f = this.filter;
      return Boolean(
        this.scope ||
          f.chip ||
          f.kind ||
          f.month ||
          f.ownerId ||
          f.search.trim() ||
          f.status !== "open",
      );
    },

    init() {
      // 검색은 서버가 찾는다 — 칠 때마다 부르지 않고 잠깐 멈추면
      (this as unknown as { $watch(k: string, fn: () => void): void }).$watch(
        "filter.search",
        () => {
          if (this.searchTimer) {
            clearTimeout(this.searchTimer);
          }
          this.searchTimer = setTimeout(() => this.applyFilter(), 300);
        },
      );
      this.focusListener = (e: Event) => this.focus((e as CustomEvent<PageScope>).detail ?? {});
      window.addEventListener(FOCUS_EVENT, this.focusListener);
    },

    destroy() {
      if (this.focusListener) {
        window.removeEventListener(FOCUS_EVENT, this.focusListener);
      }
    },

    kindLabel: (k: string) => KIND_LABELS[k] ?? k,
    isRecordKind,
    statusLabel: (s: string) => STATUS_LABELS[s] ?? s,
    statusTone: (s: string) => STATUS_TONES[s] ?? "ds-label--gray",
    originLabel: (o: string) => ORIGIN_LABELS[o] ?? o,
    describeAction,
    changeParts,
    EMPTY_CHANGE_VALUE,
    primaryActionFor,
    get months(): Array<{ value: string; label: string }> {
      return monthOptions(this.today);
    },
    /** 칸 하나에 「전 → 후」 한 줄 — 전 값을 모르면 후만 */
    changeLine(part: ChangePart): string {
      if (!part.after) {
        return part.label;
      }
      return part.before === null
        ? `${part.label} → ${part.after}`
        : `${part.label} ${part.before} → ${part.after}`;
    },
    primaryLabel(row: ScheduledActionRow): string {
      switch (primaryActionFor(row)) {
        case "acknowledge":
          return "확인함";
        case "run":
          return this.runLabel(row);
        case "approve":
          return "검토";
        case "dryRun":
          return "미리보기";
        case "detail":
          return "상세";
        default:
          return "";
      }
    },
    /** 주 동작이 「처리」인가(검은 버튼) — 예약됨의 검사·끝난 건의 상세는 보조(테두리 버튼) */
    isUrgent(row: ScheduledActionRow): boolean {
      const a = primaryActionFor(row);
      return a === "run" || a === "approve" || a === "acknowledge";
    },
    doPrimary(row: ScheduledActionRow) {
      switch (primaryActionFor(row)) {
        case "acknowledge":
          return this.acknowledge(row);
        case "run":
          return this.runNow(row);
        case "approve":
          return this.approve(row);
        case "dryRun":
          return this.dryRun(row);
        default:
          return this.openDetail(row);
      }
    },
    /**
     * 더보기 열 폭 — 지금 쪽에서 아이콘이 가장 많은 줄에 딱 맞춘다(아이콘 24 · 간격 2 · 칸 고정 안쪽 여백 6×2).
     * 고정 폭을 두면 아이콘이 적은 쪽에서 좌우가 빈다.
     */
    /** 표 최소 폭 — 고정 열(상태 116 · 대상 · 시행일 132 · 소유자 120 · 작업 104 · 더보기) 합 + 「바뀌는 것」 최소 260.
     *  좁은 화면에서 「바뀌는 것」만 줄어들어 더보기 열이 표 밖으로 밀리지 않는다. 열 폭은 colgroup 과 맞출 것 */
    get tableMinWidth(): number {
      return 116 + this.targetColWidth + 132 + 120 + 104 + this.actionsColWidth + 260;
    },
    get actionsColWidth(): number {
      // 최소 2개분 — 아이콘이 없거나 하나뿐인 쪽에서도 「더보기」 머리글이 잘리지 않게
      const most = Math.max(
        2,
        ...this.rows.map((r) => rowMenuItems(r, this.today).filter((m) => m.enabled).length),
      );
      return most * 24 + (most - 1) * 2 + 12;
    },
    /** 「대상」 열을 내용 폭에 맞춘다 — 표가 table-layout:fixed 라 브라우저가 대신 재 주지 않는다.
     *  그려진 이름·사번·머리글을 그 글꼴 그대로 캔버스로 재서 가장 긴 것 + 칸 좌우 여백. */
    fitTargetCol(table: HTMLElement) {
      requestAnimationFrame(() => {
        const ctx = measureCanvas();
        if (!ctx) {
          return;
        }
        let widest = 0;
        for (const el of table.querySelectorAll<HTMLElement>("[data-target-measure]")) {
          const text = el.textContent?.trim() ?? "";
          if (!text) {
            continue;
          }
          const cs = getComputedStyle(el);
          ctx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
          widest = Math.max(widest, ctx.measureText(text).width);
        }
        const cell = table.querySelector<HTMLElement>("[data-target-col]");
        if (!cell || widest === 0) {
          return;
        }
        const cs = getComputedStyle(cell);
        const pad = Number.parseFloat(cs.paddingLeft) + Number.parseFloat(cs.paddingRight);
        // +2 — 글자 폭 반올림 · 이름 버튼 테두리 여유(꼭 맞으면 한 글자가 다음 줄로 넘어간다)
        this.targetColWidth = Math.ceil(widest + pad) + 2;
      });
    },
    rowMenu(row: ScheduledActionRow): RowMenuItem[] {
      return rowMenuItems(row, this.today);
    },
    runMenu(row: ScheduledActionRow, key: RowMenuKey) {
      switch (key) {
        case "dryRun":
          return this.dryRun(row);
        case "run":
          return this.runNow(row);
        case "approve":
          return this.approve(row);
        case "acknowledge":
          return this.acknowledge(row);
        case "openSource":
          return this.openSource(row);
        case "transfer":
          return this.openTransfer(row);
        case "cancel":
          return this.cancel(row);
      }
    },
    canCancel(row: ScheduledActionRow): boolean {
      return !isRecordKind(row.kind) && ["scheduled", "proposed", "failed"].includes(row.status);
    },
    /** 이 줄에 지금 걸린 사유·안내 — 실패는 빨강, 확인 대기는 노랑, 시트 대기·건너뜀은 안내 */
    reasonTone(row: ScheduledActionRow): "danger" | "warning" | "info" | null {
      if (!row.lastError?.message || row.lastError.acknowledgedAt) {
        return null;
      }
      if (row.status === "failed") {
        return "danger";
      }
      if (row.status === "proposed") {
        return "warning";
      }
      return row.status === "scheduled" || row.status === "skipped" ? "info" : null;
    },
    /** 「10/7 기준 적용 가능」 — 마지막 미리보기 결과, 시행일 아래 작은 줄. 기록 종류·끝난 건은 미리보기가 없다 */
    checkLine(row: ScheduledActionRow): { text: string; tone: "ok" | "bad" | "none" } | null {
      if (isRecordKind(row.kind) || !["scheduled", "proposed", "failed"].includes(row.status)) {
        return null;
      }
      if (!(row.lastDryRunResult && row.lastDryRunAt)) {
        return { text: "확인 전", tone: "none" };
      }
      // 짧은 「MM/DD」 — 화면 형식이 아니라 ISO 에서 직접 자른다
      const d = this.fmtDateIso(row.lastDryRunAt).slice(5).replace("-", "/");
      return row.lastDryRunResult.ok
        ? { text: `${d} 기준 적용 가능`, tone: "ok" }
        : { text: `${d} 기준 실패 예상`, tone: "bad" };
    },
    /** 시행일 아래 「N일 남음 / 오늘 / N일 지남」 — 진행 중만 */
    dueLine(row: ScheduledActionRow): string {
      if (!["scheduled", "proposed", "failed"].includes(row.status)) {
        return "";
      }
      const eff = String(row.effectiveDate).slice(0, 10);
      const diff = Math.round(
        (Date.parse(`${eff}T00:00:00Z`) - Date.parse(`${this.today}T00:00:00Z`)) / 86_400_000,
      );
      return diff > 0 ? `${diff}일 남음` : diff === 0 ? "오늘" : `${-diff}일 지남`;
    },

    async openDetail(row: ScheduledActionRow) {
      this.detailRow = row;
      this.detail = null;
      this.detailLoading = true;
      try {
        this.detail = (await this.sdk.scheduledAction.detail(
          row.id,
        )) as unknown as ScheduledActionDetail;
      } catch (err) {
        this.detailRow = null;
        showError(errorMessage(err, "상세를 불러오지 못했어요."));
      } finally {
        this.detailLoading = false;
      }
    },
    closeDetail() {
      this.detailRow = null;
      this.detail = null;
    },
    /**
     * 내보내기·계산용 날짜(YYYY-MM-DD). 시행일은 날짜 그대로(조직 달력). 시각 값(검사 시각)은 조직 시간대로 —
     * UTC 로 자르면 오전 0~9시가 전날이 된다. 화면 표시는 fmtDate 를 쓴다.
     */
    fmtDateIso: (v: string | Date | null | undefined) => {
      if (!v) {
        return "-";
      }
      if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
        return v;
      }
      // 시각 값은 조직 시간대의 날짜로(YYYY-MM-DD) — 시행일과 같은 모양으로 맞춘다
      return Alpine.store("hereby").formatDateISO(v as string);
    },
    /** 화면용 날짜 「2026.10.13.」 — 날짜만 있는 시행일은 시간대 변환 없이, 시각 값은 조직 시간대의 날짜로 */
    fmtDate: (v: string | Date | null | undefined) => {
      if (!v) {
        return "-";
      }
      return Alpine.store("hereby").formatDate(v as string);
    },

    /** 필터 줄을 바꿨다 — 첫 쪽부터 */
    applyFilter() {
      this.page = 1;
      return this.refresh();
    },

    /** 페이지 크기 바꾸기 — 1쪽으로 되돌려 다시 부르고 브라우저에 기억한다 */
    setPageSize(value: string | number) {
      const n = Number(value);
      if (!PAGE_SIZE_OPTIONS.some((o) => o.value === n)) {
        return;
      }
      this.pageSize = n;
      try {
        localStorage.setItem(PAGE_SIZE_KEY, String(n));
      } catch {
        // 저장 못 해도 이번 화면에서는 바뀐 크기로 본다
      }
      return this.applyFilter();
    },
    /** 「전체」인데 1,000건을 넘으면 요약 툴팁으로 잘림을 알린다 */
    get pageSizeCapTitle(): string {
      return this.pageSize >= PAGE_SIZE_ALL && this.total > PAGE_SIZE_ALL
        ? `전체 ${this.total.toLocaleString()}건 중 최대 ${PAGE_SIZE_ALL.toLocaleString()}건까지만 표시돼요`
        : "";
    },

    async goPage(p: number) {
      if (p < 1 || p > this.totalPages || p === this.page) {
        return;
      }
      this.page = p;
      await this.refresh();
    },

    async refresh(): Promise<void> {
      this.loadedOnce = true;
      if (!this.canSeePage) {
        this.rows = [];
        this.total = 0;
        this.summary = null;
        return;
      }
      this.loading = true;
      this.loadError = "";
      const seq = ++this.requestSeq;
      try {
        const [res, summary] = await Promise.all([
          this.sdk.scheduledAction.page(
            buildPageInput(this.filter, this.scope, this.page, this.pageSize) as never,
          ),
          this.sdk.scheduledAction.summary(),
        ]);
        if (seq !== this.requestSeq) {
          return;
        }
        const out = res as unknown as {
          items: ScheduledActionRow[];
          total: number;
          owners: Array<{ id: string; name: string }>;
        };
        this.rows = out.items;
        this.total = out.total;
        this.owners = out.owners;
        this.summary = summary as ScheduledChangesSummary;
        // 뒤쪽 쪽에서 건이 줄어 빈 쪽이 되면 마지막 쪽으로
        if (this.rows.length === 0 && this.page > 1 && this.total > 0) {
          this.page = this.totalPages;
          return this.refresh();
        }
      } catch (err) {
        if (seq !== this.requestSeq) {
          return;
        }
        // 오류를 「예약 없음」으로 보이지 않게 — 목록 자리에 오류와 「다시 시도」를 둔다
        this.rows = [];
        this.total = 0;
        this.loadError = errorMessage(err, "예약된 변경을 불러오지 못했어요.");
      } finally {
        if (seq === this.requestSeq) {
          this.loading = false;
        }
      }
      if (seq !== this.requestSeq) {
        return;
      }
      if (!this.overviewMonth) {
        const today = Alpine.store("hereby").todayLocal() as string;
        this.overviewMonth = today.slice(0, 7);
      }
      await this.loadOverview();
    },

    /** 알림(한 건)·시트 화면(직원·시트)에서 들어왔다 — 필터 줄은 비우고 그 범위만 보인다 */
    focus(scope: PageScope) {
      this.view = "list";
      this.scope = scope.id || scope.employeeId || scope.sheetSlug ? scope : null;
      this.filter = {
        chip: "",
        kind: "",
        status: scope.id ? "all" : "open",
        month: "",
        ownerId: "",
        search: "",
      };
      this.page = 1;
      return this.refresh();
    },

    clearScope() {
      this.scope = null;
      this.filter.status = "open";
      return this.applyFilter();
    },

    /** 상단 칩 — 건수 묶음만 보기. 「전체」(빈 칩)는 진행 중 전체로 */
    setChip(chip: SummaryChip) {
      this.scope = null;
      this.filter.chip = chip;
      this.filter.status = chip === "failed" ? "failed" : chip === "proposed" ? "proposed" : "open";
      return this.applyFilter();
    },

    /**
     * 위쪽 탭 — 연차 탭(연차 현황 · 출근율 · 연차/휴가 결재)과 같은 공용 SubTab. 건수는 필터와 무관한 서버 요약을
     * 탭 이름 뒤 작은 글자(trailingText)로 — 0건이면 숫자를 감춘다.
     */
    get statusTabs(): Array<{ id: StatusTab; label: string; trailingText?: string }> {
      const s = this.summary;
      const n = (v: number | undefined) => (v ? String(v) : undefined);
      return [
        {
          id: "open",
          label: "진행 중",
          trailingText: s ? n(s.failed + s.proposed + s.scheduled) : undefined,
        },
        { id: "failed", label: "실패", trailingText: n(s?.failed) },
        { id: "proposed", label: "확인 대기", trailingText: n(s?.proposed) },
        { id: "dueSoon", label: "7일 안에 시행", trailingText: n(s?.dueSoon) },
        { id: "ownerUnavailable", label: "이관 필요", trailingText: n(s?.ownerUnavailable) },
        { id: "done", label: "끝난 것" },
        // 이 달에 시행되는 원천 변경 — 예전엔 표 아래 붙어 있었는데, 표가 화면을 채우며 안에서 스크롤하게 되어 탭으로
        ...(this.canSeeOverview ? [{ id: "month" as const, label: "이 달 시행 요약" }] : []),
      ];
    },
    get activeStatusTab(): StatusTab {
      if (this.view === "month") {
        return "month";
      }
      if (this.filter.chip) {
        return this.filter.chip as StatusTab;
      }
      return this.filter.status === "applied" ? "done" : "open";
    },
    selectStatusTab(id: StatusTab) {
      if (id === "month") {
        this.view = "month";
        return this.loadOverview();
      }
      this.view = "list";
      if (id === "done") {
        this.scope = null;
        this.filter.chip = "";
        this.filter.status = "applied";
        return this.applyFilter();
      }
      return this.setChip(id === "open" ? "" : id);
    },

    /** 「이 달 시행 요약」 월 — DS 커스텀 월 선택기에서 고른 YYYY-MM */
    setOverviewMonth(month: string) {
      if (!/^\d{4}-\d{2}$/.test(month)) {
        return;
      }
      this.overviewMonth = month;
      return this.loadOverview();
    },
    /** ◀ ▶ — 한 달씩 */
    shiftOverviewMonth(delta: number) {
      const base = this.overviewMonth || this.today.slice(0, 7);
      const total = Number(base.slice(0, 4)) * 12 + (Number(base.slice(5, 7)) - 1) + delta;
      const y = Math.floor(total / 12);
      const m = (total % 12) + 1;
      return this.setOverviewMonth(`${y}-${String(m).padStart(2, "0")}`);
    },

    async loadOverview() {
      if (!(this.canSeeOverview && /^\d{4}-\d{2}$/.test(this.overviewMonth))) {
        this.overview = null;
        return;
      }
      this.overviewLoading = true;
      try {
        this.overview = (await this.sdk.scheduledAction.monthOverview(
          Number(this.overviewMonth.slice(0, 4)),
          Number(this.overviewMonth.slice(5, 7)),
        )) as unknown as Record<string, unknown>;
      } catch (err) {
        showError(errorMessage(err, "이 달 시행 변경을 불러오지 못했어요."));
      } finally {
        this.overviewLoading = false;
      }
    },

    /** 확인창에 넣는 대상 한 줄 — 어느 예약인지 눈으로 다시 보게 */
    targetLine(row: ScheduledActionRow): string {
      const who = row.employeeName
        ? `${row.employeeName}${row.employeeNumber ? `(${row.employeeNumber})` : ""}`
        : "직원 없음";
      return `${who} · ${this.kindLabel(row.kind)} · ${describeAction(row)} · ${this.fmtDate(row.effectiveDate)} 시행`;
    },

    async cancel(row: ScheduledActionRow) {
      if (
        !(await askConfirm({
          title: "예약을 취소할까요?",
          details: [this.targetLine(row)],
          message: "취소하면 시행일에 아무것도 바뀌지 않아요.",
          confirmLabel: "예약 취소",
          cancelLabel: "닫기",
          tone: "danger",
        }))
      ) {
        return;
      }
      await this.act(row.id, () => this.sdk.scheduledAction.cancel(row.id), "취소하지 못했어요.");
    },

    /** 승인(D7) — AI 제안(확인 대기, 변경 세트 없음)을 예약으로. 시행일이 지났으면 서버가 바로 실행한다. */
    canApprove(row: ScheduledActionRow): boolean {
      // 변경 세트가 붙은 확인 대기(정책 confirm)도 승인하면 서버가 그 변경 세트를 바로 적용한다(MED-825 QA)
      return row.status === "proposed";
    },

    async approve(row: ScheduledActionRow) {
      if (
        !(await this.confirmWithPreview(row, {
          title: "승인할까요?",
          message: row.resultRef?.sheetChangeSetId
            ? "승인하면 지금 시트에 적용돼요(내 권한으로 변경 세트 적용)."
            : `승인하면 예약되고 시행일에 소유자(${row.ownerName ?? "-"}) 권한으로 적용돼요.`,
          confirmLabel: "승인",
          when: row.resultRef?.sheetChangeSetId ? "now" : "effective",
        }))
      ) {
        return;
      }
      await this.act(
        row.id,
        async () => {
          const ran = (await this.sdk.scheduledAction.approve(row.id)) as {
            status: string;
            lastError?: { message: string } | null;
          };
          if (ran.status === "failed") {
            showError(`실패: ${ran.lastError?.message ?? "사유 없음"}`);
          }
        },
        "승인하지 못했어요.",
      );
    },

    async runNow(row: ScheduledActionRow) {
      const reschedule = row.status === "failed" && this.isFutureContract(row);
      const message = reschedule
        ? `지난 실패 사유: ${row.lastError?.message ?? "-"}\n지금 적용하지 않고, 시행일(${this.fmtDate(row.effectiveDate)})에 자동으로 적용되도록 다시 예약해요.`
        : row.status === "failed"
          ? `지난 실패 사유: ${row.lastError?.message ?? "-"}\n적용되면 되돌릴 수 없어요.`
          : "시행일을 기다리지 않고 지금 적용해요. 적용되면 되돌릴 수 없어요.";
      if (reschedule) {
        // 다시 예약은 지금 바꾸지 않아 미리보기 없이 공용 확인 창
        if (
          !(await askConfirm({
            title: `${this.runLabel(row)}할까요?`,
            details: [this.targetLine(row)],
            message,
            confirmLabel: this.runLabel(row),
            cancelLabel: "닫기",
          }))
        ) {
          return;
        }
      } else if (
        !(await this.confirmWithPreview(row, {
          title: `${this.runLabel(row)}할까요?`,
          message,
          confirmLabel: this.runLabel(row),
        }))
      ) {
        return;
      }
      await this.act(
        row.id,
        async () => {
          const ran = (await this.sdk.scheduledAction.run(row.id)) as {
            status: string;
            lastError?: { message: string } | null;
          };
          if (ran.status === "failed") {
            showError(`실패: ${ran.lastError?.message ?? "사유 없음"}`);
          } else if (ran.status === "scheduled" && this.isFutureContract(row)) {
            showAlert(
              `시행일(${this.fmtDate(row.effectiveDate)})에 자동으로 적용되도록 다시 예약했어요.`,
            );
          } else if (ran.lastError?.message && ran.status !== "applied") {
            showAlert(ran.lastError.message);
          }
        },
        "실행하지 못했어요.",
      );
    },

    async dryRun(row: ScheduledActionRow) {
      await this.act(
        row.id,
        async () => {
          const r = (await this.sdk.scheduledAction.dryRun(row.id)) as DryRunResult;
          // 확인 버튼 자리는 「닫기」 하나 — 아무것도 적용하지 않는 창이다
          showAlert({
            title: "미리보기",
            message: "",
            details: [this.targetLine(row)],
            ...previewDialogBody(r, null),
            // 미리보기는 끝까지 돌려 보고 되돌린다 — 사용자가 「적용됐나?」로 오해하지 않게 못 박는다
            footnote: "미리보기라 실제로는 아무것도 바뀌지 않았어요.",
            confirmLabel: "닫기",
          });
        },
        "미리보기를 하지 못했어요.",
      );
    },

    /** 자동 처리 기록의 실패 — 원래 화면에서 처리했으면 「확인함」으로 목록·건수에서 뺀다 */
    async acknowledge(row: ScheduledActionRow) {
      if (
        !(await askConfirm({
          title: "확인함으로 넘길까요?",
          details: [this.targetLine(row)],
          message:
            "원래 화면(결재·연차·근로시간)에서 처리했으면 확인함으로 넘겨 진행 중 목록에서 빼요. 같은 일이 다시 실패하면 다시 보여요.",
          confirmLabel: "확인함",
          cancelLabel: "닫기",
        }))
      ) {
        return;
      }
      await this.act(
        row.id,
        () => this.sdk.scheduledAction.acknowledge(row.id),
        "확인 처리하지 못했어요.",
      );
    },

    /** 원래 화면 — 칸 예약은 그 시트, 계약 변경은 그 직원 정보 */
    canOpenSource(row: ScheduledActionRow): boolean {
      return (
        (row.kind === "sheet_cell" &&
          Boolean(row.resultRef?.sheetViewId ?? row.target?.sheetViewId)) ||
        (row.kind === "contract_change" && Boolean(row.employeeId))
      );
    },

    async openSource(row: ScheduledActionRow) {
      if (row.kind === "sheet_cell") {
        const sheetViewId = (row.resultRef?.sheetViewId ?? row.target?.sheetViewId) as
          | string
          | undefined;
        if (sheetViewId) {
          window.dispatchEvent(new CustomEvent(OPEN_SHEET_EVENT, { detail: { sheetViewId } }));
        }
        return;
      }
      if (row.kind === "contract_change" && row.employeeId) {
        try {
          const emp = await this.sdk.employee.getEmployee(row.employeeId);
          window.dispatchEvent(new CustomEvent("edit-employee-open", { detail: emp }));
        } catch (err) {
          showError(errorMessage(err, "직원 정보를 열지 못했어요."));
        }
      }
    },

    /** 지금 조건 그대로 전부 — 서버 쪽을 200건씩 받아 CSV 로 */
    async exportCsv() {
      this.exporting = true;
      try {
        const all: ScheduledActionRow[] = [];
        for (let p = 1; p <= 50; p++) {
          const res = (await this.sdk.scheduledAction.page(
            buildPageInput(this.filter, this.scope, p, 200) as never,
          )) as unknown as { items: ScheduledActionRow[]; total: number };
          all.push(...res.items);
          if (all.length >= res.total || res.items.length === 0) {
            break;
          }
        }
        const csv = toCsv([
          [
            "상태",
            "종류",
            "출처",
            "직원",
            "사번",
            "바뀌는 것",
            "메모",
            "시행일",
            "소유자",
            "사유",
            "만든 날",
          ],
          ...all.map((r) => [
            this.statusLabel(r.status),
            this.kindLabel(r.kind),
            this.originLabel(r.origin),
            r.employeeName ?? "",
            r.employeeNumber ?? "",
            describeAction(r),
            r.note ?? "",
            this.fmtDateIso(r.effectiveDate),
            r.ownerName ?? "",
            r.lastError?.message ?? "",
            this.fmtDateIso(r.createdAt),
          ]),
        ]);
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `예약된변경_${Alpine.store("hereby").todayLocal()}.csv`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (err) {
        showError(errorMessage(err, "내보내지 못했어요."));
      } finally {
        this.exporting = false;
      }
    },

    openTransfer(row: ScheduledActionRow) {
      this.transferTarget = row;
      this.transferSearch = "";
      this.transferCandidates = [];
    },

    async searchTransferCandidates() {
      const q = this.transferSearch.trim();
      if (q.length < 1) {
        this.transferCandidates = [];
        return;
      }
      try {
        const res = (await this.sdk.employee.getEmployees({
          search: q,
          page: 1,
          limit: 10,
        } as never)) as unknown as {
          items?: Array<{
            id: string;
            firstName: string;
            lastName: string;
            employeeNumber: string;
          }>;
        };
        this.transferCandidates = (res.items ?? [])
          .filter((e) => e.id !== this.transferTarget?.ownerId)
          .map((e) => ({
            id: e.id,
            name: `${e.lastName ?? ""}${e.firstName ?? ""}`,
            employeeNumber: e.employeeNumber,
          }));
      } catch (err) {
        showError(errorMessage(err, "직원을 찾지 못했어요."));
      }
    },

    async transferTo(candidate: { id: string; name: string }) {
      const row = this.transferTarget;
      if (!row) {
        return;
      }
      if (
        !(await askConfirm({
          title: "소유자를 이관할까요?",
          details: [this.targetLine(row)],
          message: `${row.ownerName ?? "-"} → ${candidate.name}. 시행일에 ${candidate.name}님의 권한으로 실행돼요(그 권한으로 검사가 통과해야 바뀌어요).`,
          confirmLabel: "이관",
          cancelLabel: "닫기",
        }))
      ) {
        return;
      }
      this.transferBusy = true;
      try {
        await this.sdk.scheduledAction.transfer(row.id, candidate.id);
        this.transferTarget = null;
        await this.refresh();
      } catch (err) {
        showError(errorMessage(err, "이관하지 못했어요."));
      } finally {
        this.transferBusy = false;
      }
    },

    /**
     * 데이터를 바꾸는 확인(다시 실행 · 지금 실행 · 검토(승인)) — 창을 띄우기 전에 적용 미리보기부터 돌려,
     * 누르기 직전에 무엇이 바뀌는지(또는 또 실패할지)를 같은 창의 표로 보게 한다.
     */
    async confirmWithPreview(
      row: ScheduledActionRow,
      opts: { title: string; message: string; confirmLabel: string; when?: "now" | "effective" },
    ): Promise<boolean> {
      let preview: DryRunResult | null = null;
      let previewError: string | null = null;
      this.busyId = row.id;
      try {
        preview = (await this.sdk.scheduledAction.dryRun(row.id)) as DryRunResult;
      } catch (err) {
        previewError = errorMessage(err, "사유 없음");
      } finally {
        this.busyId = "";
      }
      return askConfirm({
        title: opts.title,
        message: opts.message,
        details: [this.targetLine(row)],
        ...previewDialogBody(preview, previewError, opts.when),
        confirmLabel: opts.confirmLabel,
        cancelLabel: "닫기",
      });
    },

    async act(id: string, fn: () => Promise<unknown>, fallback: string) {
      this.busyId = id;
      try {
        await fn();
        await this.refresh();
        // 상세 창에서 처리했으면 그 건을 다시 읽어 상태·이력을 바로 맞춘다
        const open = this.detailRow;
        if (open && open.id === id) {
          await this.openDetail(this.rows.find((r) => r.id === id) ?? open);
        }
      } catch (err) {
        showError(errorMessage(err, fallback));
      } finally {
        this.busyId = "";
      }
    },
  };
}
